// The shared condition language (decision 0050's "conditions in the shared language", 0054): a typed predicate union
// over named facts, evaluated against fact sources each module registers by name. Quests, ink, barks, shops and
// triggers all ask the same questions the same way ("affinity.child >= 3", "quest.main == done"). A fact id is dotted:
// its first part names the source (`affinity`, `item`, `quest`…), the rest is the source's own key (`child`,
// `copper-ore`, `main.objective`). Sources say which keys they know, so a content check can reject a condition that
// names a fact no one answers (suggestion: a typed predicate evaluator, audio-ui-content.md). Strings parse into the
// union (`parseCondition`), so authors write `"item.copper-ore >= 2 and not story.met-elder"`.

/** A fact's value. */
export type FactValue = number | string | boolean | null;

/** A comparison. */
export type CompareOp = "==" | "!=" | ">" | ">=" | "<" | "<=";

/** A condition: a fact (truthy, or compared to a value), or all / any / not of others. */
export type Condition =
  | { fact: string; op?: CompareOp; value?: FactValue }
  | { all: Condition[] }
  | { any: Condition[] }
  | { not: Condition };

/** A source of facts under one name: `get(key)` for `name.key` (key "" for the bare `name`). */
export interface FactSource {
  /** The fact ids' first part, e.g. "affinity" for `affinity.child`. */
  name: string;
  /** The value of `name.key` (key "" for the bare name). Throws or returns null for a key it doesn't answer. */
  get(key: string): FactValue;
  /** Whether it answers `key` (for content checks). Default: every key. */
  knows?(key: string): boolean;
  /** Example keys (for a report and for error messages). */
  keys?(): readonly string[];
}

/** A condition that can't be evaluated: a fact no source answers. */
export class UnknownFactError extends Error {
  constructor(readonly fact: string, message: string) {
    super(message);
    this.name = "UnknownFactError";
  }
}

const OPS: readonly CompareOp[] = ["==", "!=", ">=", "<=", ">", "<"];

/** JS-style truth for a fact: non-zero numbers, `true` and non-empty strings are true. */
export const truthy = (v: FactValue): boolean => (typeof v === "number" ? v !== 0 : typeof v === "string" ? v.length > 0 : v === true);

/** Compare two fact values. Numbers compare as numbers; anything else by equality only (order ops are false). */
export function compare(a: FactValue, op: CompareOp, b: FactValue): boolean {
  if (op === "==") return a === b || (typeof a === "number" && typeof b === "number" && a === b) || (a === null && b === null);
  if (op === "!=") return !compare(a, "==", b);
  const x = typeof a === "boolean" ? Number(a) : a;
  const y = typeof b === "boolean" ? Number(b) : b;
  if (typeof x !== "number" || typeof y !== "number") return false;
  return op === ">" ? x > y : op === ">=" ? x >= y : op === "<" ? x < y : x <= y;
}

/** Split a fact id into its source name and key. */
export function splitFact(fact: string): { name: string; key: string } {
  const i = fact.indexOf(".");
  return i < 0 ? { name: fact, key: "" } : { name: fact.slice(0, i), key: fact.slice(i + 1) };
}

/** Every fact id a condition names. */
export function factsOf(c: Condition): string[] {
  if ("fact" in c) return [c.fact];
  if ("all" in c) return c.all.flatMap(factsOf);
  if ("any" in c) return c.any.flatMap(factsOf);
  return factsOf(c.not);
}

/** Facts answered by named sources; conditions evaluated over them. */
export class Conditions {
  private readonly sources = new Map<string, FactSource>();

  /** Add (or replace) a source. Returns this. */
  register(source: FactSource): this {
    this.sources.set(source.name, source);
    return this;
  }

  /** Remove a source by name. */
  unregister(name: string): void {
    this.sources.delete(name);
  }

  /** The source names registered. */
  names(): string[] {
    return [...this.sources.keys()].sort();
  }

  /** A source by name, if any. */
  source(name: string): FactSource | undefined {
    return this.sources.get(name);
  }

  /** Whether some source answers `fact`. */
  knows(fact: string): boolean {
    const { name, key } = splitFact(fact);
    const s = this.sources.get(name);
    return !!s && (s.knows ? s.knows(key) : true);
  }

  /** A fact's value now. Throws {@link UnknownFactError} when no source answers it. */
  fact(fact: string): FactValue {
    const { name, key } = splitFact(fact);
    const s = this.sources.get(name);
    if (!s || (s.knows && !s.knows(key))) throw new UnknownFactError(fact, `unknown fact "${fact}" (sources: ${this.names().join(", ") || "none"})`);
    const v = s.get(key);
    return v === undefined ? null : v;
  }

  /** Evaluate a condition (or its string form). Throws on an unknown fact. */
  test(c: Condition | string): boolean {
    const cond = typeof c === "string" ? parseCondition(c) : c;
    if ("fact" in cond) {
      const v = this.fact(cond.fact);
      return cond.op === undefined ? truthy(v) : compare(v, cond.op, cond.value ?? null);
    }
    if ("all" in cond) return cond.all.every((x) => this.test(x));
    if ("any" in cond) return cond.any.some((x) => this.test(x));
    return !this.test(cond.not);
  }

  /** Problems with a condition (unknown facts, a string that won't parse); empty when it is sound. */
  check(c: Condition | string): string[] {
    let cond: Condition;
    try {
      cond = typeof c === "string" ? parseCondition(c) : c;
    } catch (e) {
      return [(e as Error).message];
    }
    return factsOf(cond)
      .filter((f) => !this.knows(f))
      .map((f) => `unknown fact "${f}"`);
  }
}

// --- the string form ------------------------------------------------------------------------------------------------
// expr := or ; or := and ("or" and)* ; and := unary ("and" unary)* ; unary := "not" unary | "(" expr ")" | atom
// atom := FACT [OP VALUE] ; VALUE := number | true | false | null | "quoted" | bare-word

type Tok = { k: "word" | "op" | "str" | "num" | "lp" | "rp"; v: string };

function lex(src: string): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i]!;
    if (/\s/.test(ch)) {
      i++;
      continue;
    }
    if (ch === "(") {
      out.push({ k: "lp", v: ch });
      i++;
      continue;
    }
    if (ch === ")") {
      out.push({ k: "rp", v: ch });
      i++;
      continue;
    }
    const op = OPS.find((o) => src.startsWith(o, i));
    if (op) {
      out.push({ k: "op", v: op });
      i += op.length;
      continue;
    }
    if (ch === '"' || ch === "'") {
      const end = src.indexOf(ch, i + 1);
      if (end < 0) throw new Error(`condition "${src}": unclosed quote`);
      out.push({ k: "str", v: src.slice(i + 1, end) });
      i = end + 1;
      continue;
    }
    const m = /^-?\d+(\.\d+)?(?![\w.-]*[A-Za-z_])/.exec(src.slice(i));
    if (m) {
      out.push({ k: "num", v: m[0] });
      i += m[0].length;
      continue;
    }
    const w = /^[A-Za-z_][\w.-]*/.exec(src.slice(i)) ?? /^[\w.-]+/.exec(src.slice(i));
    if (!w) throw new Error(`condition "${src}": unexpected "${ch}"`);
    out.push({ k: "word", v: w[0] });
    i += w[0].length;
  }
  return out;
}

/** Parse a condition's string form, e.g. `"affinity.child >= 3 and not story.met-elder"`. Throws on bad syntax. */
export function parseCondition(src: string): Condition {
  const toks = lex(src);
  let p = 0;
  const peek = (): Tok | undefined => toks[p];
  const isWord = (w: string): boolean => peek()?.k === "word" && peek()!.v === w;
  const fail = (what: string): never => {
    throw new Error(`condition "${src}": ${what}`);
  };
  const value = (): FactValue => {
    const t = toks[p++];
    if (!t) return fail("a value is missing");
    if (t.k === "num") return Number(t.v);
    if (t.k === "str") return t.v;
    if (t.k === "word") return t.v === "true" ? true : t.v === "false" ? false : t.v === "null" ? null : t.v;
    return fail(`expected a value, got "${t.v}"`);
  };
  const unary = (): Condition => {
    if (isWord("not")) {
      p++;
      return { not: unary() };
    }
    if (peek()?.k === "lp") {
      p++;
      const e = or();
      if (peek()?.k !== "rp") fail("missing )");
      p++;
      return e;
    }
    const t = toks[p++];
    if (!t || t.k !== "word") return fail(`expected a fact, got "${t?.v ?? "the end"}"`);
    if (peek()?.k === "op") {
      const op = toks[p++]!.v as CompareOp;
      return { fact: t.v, op, value: value() };
    }
    return { fact: t.v };
  };
  const and = (): Condition => {
    const parts = [unary()];
    while (isWord("and")) {
      p++;
      parts.push(unary());
    }
    return parts.length === 1 ? parts[0]! : { all: parts };
  };
  const or = (): Condition => {
    const parts = [and()];
    while (isWord("or")) {
      p++;
      parts.push(and());
    }
    return parts.length === 1 ? parts[0]! : { any: parts };
  };
  if (!toks.length) return fail("empty");
  const c = or();
  if (p < toks.length) fail(`unexpected "${toks[p]!.v}"`);
  return c;
}

/** A source over a plain record (tests, stand-ins): `name.key` reads `values[key]`; knows only the keys it has unless
 *  `open` (then unknown keys read `fallback`). */
export function recordSource(name: string, values: Record<string, FactValue>, o: { open?: boolean; fallback?: FactValue } = {}): FactSource {
  return {
    name,
    get: (key) => (key in values ? values[key]! : (o.fallback ?? null)),
    knows: (key) => o.open === true || key in values,
    keys: () => Object.keys(values),
  };
}
